import {
    action,
    computed,
    observable,
    makeObservable,
} from 'mobx';

import AuthSettingsStore from '../AuthSettingsStore';
import {
    DropdownItem,
    Policy,
    PolicySetting,
    PolicySettingsAll,
    PolicySettingsGroup,
} from '../../types/types';
import { FIVE_SECONDS_IN_MILLI } from './options';
import { BASEURL, ENDPOINTS } from '../../api';
import isEqual from 'lodash/isEqual';
import differenceWith from 'lodash/differenceWith';

import { captureErrorForSentry, checkIs404, checkIsSpecificCode, clearMultipleSpaces } from '../../components/utils';
import { PolicySimpleWithMFA } from '../UploadFilesStore/interfaces';
import { preparePolicyForEditor, editPolicy, expandSettings, validateRequiredSettings as validateRequiredSettingsHelper, getDaysRangeSetting, getLimitToUsersSetting, getLimitToUsersValueSetting } from './helpers';
import { DAYS_RETENTION_RANGE } from './dataRetention';

interface FetchPolicyListPayload {
    isSilently?: boolean;
    forceRefresh?: boolean;
}

const FETCH_POLICY_LIST_PAYLOAD_DEFAULT: FetchPolicyListPayload = { isSilently: false };

export interface PolicyOperationResult {
    success: boolean;
    error?: string;
    errorCode?: number;
    policy?: Policy;
}

export interface PolicyValidationError {
    type: 'nameError' | 'validationError' | 'rangeError';
    message: string;
    messageKey?: string;
}

interface RetryConfig {
    maxRetries: number;
    baseDelay: number;
    maxDelay: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
    maxRetries: 3,
    baseDelay: 1000, // 1 second
    maxDelay: 10000, // 10 seconds
};

/**
 * Utility to retry async operations with exponential backoff
 */
const retryWithBackoff = async <T>(
    operation: () => Promise<T>,
    config: RetryConfig = DEFAULT_RETRY_CONFIG,
    operationName = 'Operation',
): Promise<T> => {
    let lastError: Error;

    for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
        try {
            return await operation();
        } catch (error) {
            lastError = error as Error;

            // Don't retry on client errors (4xx) or if we're out of retries
            const statusCode = (error as any)?.status || (error as any)?.statusCode;
            const isClientError = statusCode >= 400 && statusCode < 500;

            if (isClientError || attempt === config.maxRetries) {
                throw error;
            }

            // Calculate delay with exponential backoff
            const delay = Math.min(
                config.baseDelay * Math.pow(2, attempt),
                config.maxDelay,
            );

            // Add some jitter to prevent thundering herd
            const jitter = Math.random() * delay * 0.1;
            const totalDelay = delay + jitter;

            console.log(`${operationName} failed (attempt ${attempt + 1}/${config.maxRetries + 1}), retrying in ${Math.round(totalDelay)}ms...`);

            await new Promise((resolve) => setTimeout(resolve, totalDelay));
        }
    }

    throw lastError!;
};

/**
 * Check if the browser is online
 */
const isOnline = (): boolean => {
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
};

class PolicyStore {
    constructor(authSettingsStore: AuthSettingsStore) {
        this.authSettingsStore = authSettingsStore;
        makeObservable(this);
    }

    private readonly authSettingsStore: AuthSettingsStore;

    @observable lastFetchTime = new Date().getTime();

    @observable hasFilePolicyError = false;

    @observable loadingPolicy = false;

    @observable policyList: Policy[] = [];

    @observable selectedPolicy: Policy = null;

    @observable changedPolicy: Policy = null;

    @observable saveAttempted = false;

    @computed
    get hasLoadingError(): boolean {
        return this.hasFilePolicyError;
    }

    @computed
    get hasSettingsInList(): boolean {
        const { policyList } = this;
        if (policyList.length) {
            const firstElement = policyList[0];
            return !!(firstElement.settings && firstElement.settings.access);
        }
        return false;
    }

    @computed
    get policiesListToDropdown(): DropdownItem[] {
        return this.policyList
            .map<DropdownItem<string>>(({ id, title }) => ({ value: id, label: title }))
            .sort((current, prev) => (
                current.label?.toLowerCase() > prev.label?.toLowerCase() ? 1 : -1
            ));
    }

    @computed
    get hasPolicies(): boolean {
        return !!this.policyList.length;
    }

    @computed
    get defaultPolicy(): Policy | null {
        return this.policyList.length
            ? this.policyList.find((policy) => policy.active) || this.policyList[0]
            : null;
    }

    @computed
    get defaultPolicyWithMFA(): PolicySimpleWithMFA {
        const { defaultPolicy } = this;
        return defaultPolicy && {
            id: defaultPolicy.id,
            title: defaultPolicy.title,
            isMfa: defaultPolicy.isMfa,
        };
    }

    @action
    setPolicyList = (policyList: Policy[]): void => {
        this.policyList = policyList;
    }

    @action
    setLastFetchTime = (lastFetchTime: number): void => {
        this.lastFetchTime = lastFetchTime;
    }

    @action
    setSelectedPolicy = (selectedPolicy: Policy): void => {
        this.selectedPolicy = selectedPolicy;
    }

    @action
    setPolicyForEditing = (policy: Policy): void => {
        const policyForEditor = preparePolicyForEditor(policy);
        this.selectedPolicy = { ...policyForEditor };
        this.changedPolicy = { ...policyForEditor };
        this.saveAttempted = false;
    }

    selectPolicyForEditing = (policyId?: string): void => {
        const policy: Policy = policyId ? this.findPolicy(policyId) : this.defaultPolicy;
        if (policy) {
            this.setPolicyForEditing(policy);
        }
    }

    @action
    setChangedPolicy = (changedPolicy: Policy): void => {
        this.changedPolicy = changedPolicy;
    }

    @action
    setEditingPolicyName = (policyName: string): void => {
        this.changedPolicy.title = policyName;
    }

    @action
    setLoadingPolicy = (val: boolean): void => {
        this.loadingPolicy = val;
    }

    fetchPolicyList = async ({
        isSilently,
        forceRefresh,
    }: FetchPolicyListPayload = FETCH_POLICY_LIST_PAYLOAD_DEFAULT): Promise<void> => {
        const { policyList } = this;
        // Immediately set states if not silently fetching
        try{

            if (!isSilently) {
                this.setHasFilePolicyError(false);
                this.setLoadingPolicy(true);
            }
        
            if (!policyList.length || forceRefresh) {
                // When store is empty, or a fresh server value is required (e.g. right after a save),
                // we actually want to wait for the response
                await this.fetchPoliciesInBackground(isSilently, forceRefresh);
            } else{
                // When store exist we prefer to fetch in the background, and the ui would be updated when the response is ready
                this.fetchPoliciesInBackground(isSilently);
            }
        
            // Return immediately if not silently fetching
            if (!isSilently) {
                this.setLoadingPolicy(false);
            }
        } catch(error){
            if(!this.policyList.length){
                captureErrorForSentry(error, 'PolicyStore.fetchPolicyList');
            }
        }

    }
    
    // New function to fetch policies in the background with retry logic
    fetchPoliciesInBackground = async (isSilently: boolean, forceRefresh?: boolean): Promise<void> => {
        const { API } = this.authSettingsStore;
        const { policyList, lastFetchTime } = this;
        let hasError = false;
    
        try {
            const currentTime = new Date().getTime();
            const timeSinceLastFetch = Math.abs(currentTime - lastFetchTime); // Difference in milliseconds
            
            if (forceRefresh || timeSinceLastFetch > FIVE_SECONDS_IN_MILLI || !policyList.length) {
                // Use retry logic for fetching policies
                const newPolicyList = await retryWithBackoff(
                    async () => API.get(BASEURL.backend(), ENDPOINTS.policies(), {}),
                    { ...DEFAULT_RETRY_CONFIG, maxRetries: 2 }, // Fewer retries for background fetch
                    'Fetch Policies',
                );
                
                this.setLastFetchTime(new Date().getTime());
                this.setPolicyList(newPolicyList);
            }
        } catch (error) {
            hasError = true;
            if (!policyList.length) {
                captureErrorForSentry(error, 'PolicyStore.fetchPoliciesInBackground');
            }
        } finally {
            if (!isSilently) {
                this.setLoadingPolicy(false);
                this.setHasFilePolicyError(hasError);
            }
        }
    }

    removePolicyFromList = (policyId: string): void => {
        this.setPolicyList(this.policyList.filter(({ id }) => id !== policyId));
    }

    tryAddPolicyToList = (policy: Policy): void => {
        if (!this.findPolicy(policy.id)) {
            this.setPolicyList([...this.policyList, policy]);
        }
    }

    @action
    setHasFilePolicyError = (val: boolean): void => {
        this.hasFilePolicyError = val;
    }

    @action
    unmount = (): void => {
        this.selectedPolicy = null;
        this.changedPolicy = null;
        this.hasFilePolicyError = false;
        this.loadingPolicy = false;
        this.saveAttempted = false;
    }

    findPolicy = (policyId: string): Policy | undefined => (
        this.policyList.find(({ id }) => id === policyId)
    )

    tryFindPolicy = async (policyId: string, forceRefresh = false): Promise<Policy | undefined> => {
        if (!forceRefresh) {
            const cachedPolicy = this.findPolicy(policyId);
            if (cachedPolicy) {
                return cachedPolicy;
            }
        }
        await this.fetchPolicyList({ isSilently: true });
        return this.findPolicy(policyId);
    }

    editPolicy = (settingId: string, groupType: PolicySettingsGroup, settingChanges: Partial<PolicySetting>, settingsToDisable?: string[]): void => {
        const newState: Policy = editPolicy(settingId, groupType, settingChanges, this.changedPolicy, settingsToDisable);
        this.setChangedPolicy(newState);
    }

    /**
     * Validate policy name for new policies
     * @returns Error info if invalid, null if valid
     */
    validatePolicyName = (policyName: string, isNewPolicy: boolean): PolicyValidationError | null => {
        if (!isNewPolicy) {
            return null;
        }

        const clearName = clearMultipleSpaces(policyName);
        if (!clearName) {
            return {
                type: 'nameError',
                message: 'policyEditor.messages.emptyName',
                messageKey: 'emptyName',
            };
        }

        const nameExists = this.policyList.find(({ title }) => clearMultipleSpaces(title) === clearName);
        if (nameExists) {
            return {
                type: 'nameError',
                message: 'policyEditor.messages.alreadyExists',
                messageKey: 'alreadyExists',
            };
        }

        return null;
    }

    /**
     * Validate days retention setting
     * @returns Error info if invalid, null if valid
     */
    validateDaysRetention = (): PolicyValidationError | null => {
        const daysRangeSetting = getDaysRangeSetting(this.changedPolicy);
        if (!daysRangeSetting) {
            return null;
        }

        const { min, max } = DAYS_RETENTION_RANGE;
        const value = daysRangeSetting.value as number;
        const isValid = value >= min && value <= max && Number.isInteger(value);

        if (!isValid) {
            return {
                type: 'rangeError',
                message: `Value must be between ${min} and ${max}`,
            };
        }

        return null;
    }

    /**
     * Validate limitToUsers setting - ensure list is not empty when enabled
     * @returns Error info if invalid, null if valid
     */
    validateLimitToUsers = (): PolicyValidationError | null => {
        const limitToUsersSetting = getLimitToUsersSetting(this.changedPolicy);
        const limitToUsersValueSetting = getLimitToUsersValueSetting(this.changedPolicy);
        
        // Check if the toggle is enabled
        const isEnabled = limitToUsersSetting && !!limitToUsersSetting.value;
        
        if (!isEnabled) {
            return null;
        }
        
        // If enabled, check if the list is empty
        const usersList = limitToUsersValueSetting?.value as string[] | undefined;
        const isEmpty = !usersList || usersList.length === 0;
        
        if (isEmpty) {
            return {
                type: 'validationError',
                message: 'general.validation.required',
                messageKey: 'required',
            };
        }
        
        return null;
    }

    /**
     * Validate required settings (e.g., at least one verification method)
     * @returns Error info if invalid, null if valid
     */
    validateRequiredSettings = (): PolicyValidationError | null => {
        const expandedPolicy = expandSettings(this.changedPolicy, 'access');
        const validationResult = validateRequiredSettingsHelper(expandedPolicy.settings.access);

        if (!validationResult.isValid && validationResult.errorMessageKey) {
            return {
                type: 'validationError',
                message: `policyEditor.messages.incompatibleSettingsWarnings.${validationResult.errorMessageKey}`,
                messageKey: validationResult.errorMessageKey,
            };
        }

        return null;
    }

    /**
     * Save or update a policy
     * Handles validation, API calls, and state updates
     * Includes offline detection and retry logic
     */
    savePolicy = async (): Promise<PolicyOperationResult> => {
        const { API } = this.authSettingsStore;
        const { selectedPolicy, changedPolicy } = this;
        const isNew = !selectedPolicy.id;

        // Mark that a save was attempted
        this.saveAttempted = true;

        // Check if online
        if (!isOnline()) {
            return {
                success: false,
                error: 'policyEditor.messages.offlineError',
                errorCode: 0,
            };
        }

        // Validate policy name for new policies
        const nameError = this.validatePolicyName(changedPolicy.title, isNew);
        if (nameError) {
            return {
                success: false,
                error: nameError.message,
                errorCode: 400,
            };
        }

        // Validate required settings
        const validationError = this.validateRequiredSettings();
        if (validationError) {
            return {
                success: false,
                error: validationError.message,
                errorCode: 400,
            };
        }

        // Expand settings for API
        const expandedSelectedPolicy = expandSettings(selectedPolicy, 'access');
        const expandedEditedPolicy = expandSettings(changedPolicy, 'access');

        // Prepare request body (only changed settings)
        const flatSettings = (settings: PolicySettingsAll): PolicySetting[] => Object.values(settings)
            .filter((setting) => !!setting).flat();

        const getResultSettings = (
            storedSettings: PolicySettingsAll,
            stateSettings: PolicySettingsAll,
            isNewPolicy: boolean,
        ): PolicySetting[] => {
            const stateSettingsFlat = flatSettings(stateSettings);
            if (isNewPolicy) {
                return stateSettingsFlat;
            }
            const storedSettingsFlat = flatSettings(storedSettings);
            return differenceWith(stateSettingsFlat, storedSettingsFlat, isEqual);
        };

        const requestBody = {
            title: clearMultipleSpaces(changedPolicy.title),
            settings: getResultSettings(
                expandedSelectedPolicy.settings,
                expandedEditedPolicy.settings,
                isNew,
            ),
        };

        try {
            this.setLoadingPolicy(true);
            let newPolicy: Policy = null;

            // Use retry logic for API calls
            await retryWithBackoff(
                async () => {
                    if (isNew) {
                        newPolicy = await API.post(
                            BASEURL.backend(),
                            ENDPOINTS.policy(),
                            { body: requestBody },
                        );
                    } else {
                        await API.patch(
                            BASEURL.backend(),
                            ENDPOINTS.policy(selectedPolicy.id),
                            { body: requestBody },
                        );
                    }
                },
                DEFAULT_RETRY_CONFIG,
                isNew ? 'Create Policy' : 'Update Policy',
            );

            // Refresh policy list with fresh server data, bypassing the short-lived cache,
            // so the editor is re-populated with the values that were just saved
            // (rather than the stale, pre-save data still sitting in policyList).
            await this.fetchPolicyList({ isSilently: true, forceRefresh: true });

            if (newPolicy) {
                this.tryAddPolicyToList(newPolicy);
            }

            // Select the saved policy
            this.selectPolicyForEditing(isNew && newPolicy ? newPolicy.id : selectedPolicy.id);

            return {
                success: true,
                policy: newPolicy || selectedPolicy,
            };
        } catch (error) {
            if (checkIsSpecificCode(error, 409)) {
                return {
                    success: false,
                    error: 'policyEditor.messages.alreadyExists',
                    errorCode: 409,
                };
            }

            captureErrorForSentry(error, 'PolicyStore.savePolicy');
            return {
                success: false,
                error: isNew ? 'policyEditor.messages.saveError' : 'policyEditor.messages.updateError',
            };
        } finally {
            this.setLoadingPolicy(false);
        }
    }

    /**
     * Delete a policy
     * Includes offline detection and retry logic
     */
    deletePolicy = async (policyId: string): Promise<PolicyOperationResult> => {
        const { API } = this.authSettingsStore;

        // Check if online
        if (!isOnline()) {
            return {
                success: false,
                error: 'policyEditor.messages.offlineError',
                errorCode: 0,
            };
        }

        try {
            this.setLoadingPolicy(true);

            // Use retry logic for API call
            await retryWithBackoff(
                async () => {
                    await API.del(BASEURL.backend(), ENDPOINTS.policy(policyId), {});
                },
                DEFAULT_RETRY_CONFIG,
                'Delete Policy',
            );

            this.removePolicyFromList(policyId);
            this.selectPolicyForEditing();

            return { success: true };
        } catch (error) {
            if (checkIs404(error)) {
                // Policy already deleted, clean up local state
                this.removePolicyFromList(policyId);
                this.selectPolicyForEditing();
                return { success: true };
            }

            captureErrorForSentry(error, 'PolicyStore.deletePolicy');
            return {
                success: false,
                error: 'policyEditor.messages.deleteError',
            };
        } finally {
            this.setLoadingPolicy(false);
        }
    }

    /**
     * Duplicate current policy as a new policy
     */
    duplicatePolicy = (): void => {
        const { selectedPolicy } = this;
        if (!selectedPolicy) {
            return;
        }

        const newPolicySnapshot: Policy = {
            ...selectedPolicy,
            title: `${selectedPolicy.title} (1)`,
            id: '',
        };
        this.setPolicyForEditing(newPolicySnapshot);
    }

    /**
     * Check if policy has unsaved changes
     */
    hasUnsavedChanges = (): boolean => {
        const { selectedPolicy, changedPolicy } = this;
        if (!selectedPolicy || !changedPolicy) {
            return false;
        }

        const flatSettings = (settings: PolicySettingsAll): PolicySetting[] => Object.values(settings)
            .filter((setting) => !!setting).flat();

        const storedSettingsFlat = flatSettings(selectedPolicy.settings);
        const stateSettingsFlat = flatSettings(changedPolicy.settings);
        const settingsDifference = differenceWith(stateSettingsFlat, storedSettingsFlat, isEqual);

        return !!settingsDifference.length;
    }
}

export default PolicyStore;
